import { Injectable, Injector } from '@angular/core';
import { HttpEvent, HttpInterceptor, HttpRequest, HttpHandler } from '@angular/common/http'

import { Observable, tap, throwError, BehaviorSubject, filter, take, switchMap, catchError, from } from 'rxjs';
import Swal from 'sweetalert2';
import { Router } from '@angular/router';
import { SpinnerService } from './shared/spinner.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { AuthenticationService } from './authentication.service';
import { MakingDataService } from './md/service/making-data.service';

@Injectable({
  providedIn: 'root'
})
export class TokenInterCepterService implements HttpInterceptor {

  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

  constructor(private injector: Injector, private router: Router, private spinner: SpinnerService, private spinner2: NgxSpinnerService) { }

  private get authService(): AuthenticationService {
    return this.injector.get(AuthenticationService);
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Skip adding auth headers to the refresh endpoint itself
    if (req.url.includes('/auth/refresh')) {
      return next.handle(req);
    }

    const tokenizedReq = this.addToken(req);
    return next.handle(tokenizedReq).pipe(
      tap({
        next: (res: any) => {
          if (res?.body?.data) {
            if (res.body.data === 'Session Expired . Please login again') {
              this.spinner.hide()
              this.spinner2.hide()
              Swal.fire({
                title: `${res.body['data']}`,
                icon: 'warning',
                showCancelButton: false,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Logout!'
              }).then(async (result: any) => {
                if (result.value) {
                  sessionStorage.clear()
                  this.router.navigate(['/login'])
                  window.location.reload()
                }
              })
              throw new Error('SESSION_EXPIRED');
            }
          }
        },
        error: () => { }
      }),
      catchError(err => {
        if (err?.status === 401) {
          // If it's a login route, we shouldn't try to refresh the token. Just show the error.
          if (req.url.includes('/login') || req.url.includes('/loginToAll')) {
            return from(this.handleAuthError(err));
          }
          return this.handle401Error(tokenizedReq, next, err);
        }
        if (err?.status === 403) {
          return from(this.handleAuthError(err));
        }
        if (err?.status === 429) {
          this.spinner.hide();
          this.spinner2.hide();
          Swal.fire({
            title: 'Too Many Requests',
            text: err?.error?.message || 'We detected too many rapid actions. Please wait a moment and try again.',
            icon: 'warning',
            confirmButtonText: 'OK'
          });
          return throwError(() => err);
        }
        return throwError(() => err);
      })
    );
  }

  private get makingDataService(): MakingDataService {
    return this.injector.get(MakingDataService);
  }

  private async handleAuthError(err: any): Promise<any> {
    this.spinner.hide();
    this.spinner2.hide();
    Swal.close();

    let errorMessage = '';
    if (err?.error?.error === 'Blocked IP') {
      errorMessage = err.error.message;
    } else if (err?.error?.data) {
      try {
        let clonedError = { ...err.error };
        let decrypted = await this.makingDataService.dataD(clonedError);
        if (decrypted && decrypted.data) {
          errorMessage = decrypted.data;
        }
      } catch (e) {
        errorMessage = err.error.data;
      }
    }

    await Swal.fire({
      title: err?.error?.error === 'Blocked IP' ? 'Access Denied' : (err?.status === 401 ? 'Authentication Failed' : 'Access Denied'),
      text: errorMessage,
      icon: 'error',
      confirmButtonText: 'OK'
    });

    if (errorMessage.toLowerCase().includes('deactivated') || err?.status === 401 || err?.error?.error === 'Blocked IP') {
      sessionStorage.clear();
      this.router.navigate(['/login']);
    }

    throw err;
  }

  private addToken(req: HttpRequest<any>): HttpRequest<any> {
    let headers: any = {};
    const token = sessionStorage.getItem('1000');
    const gatewayId = sessionStorage.getItem('1001');
    const accountId = sessionStorage.getItem('1005');

    if (token) headers['1000'] = `bearer ${token}`;
    if (gatewayId) headers['1001'] = `${gatewayId}`;
    if (accountId) headers['1005'] = `${accountId}`;

    return req.clone({
      setHeaders: headers
    });
  }

  private handle401Error(req: HttpRequest<any>, next: HttpHandler, originalErr: any): Observable<HttpEvent<any>> {
    const refreshToken = sessionStorage.getItem('_rt');
    if (!refreshToken) {
      return from(this.handleAuthError(originalErr));
    }

    if (this.isRefreshing) {
      // Queue concurrent requests while refresh is in progress
      return this.refreshTokenSubject.pipe(
        filter(token => token !== null),
        take(1),
        switchMap(() => next.handle(this.addToken(req)))
      );
    }

    this.isRefreshing = true;
    this.refreshTokenSubject.next(null);

    return this.authService.refreshAccessToken(refreshToken).pipe(
      switchMap((resp: any) => {
        this.isRefreshing = false;
        sessionStorage.setItem('1000', resp.token);
        if (resp.refreshToken) sessionStorage.setItem('_rt', resp.refreshToken);
        this.refreshTokenSubject.next(resp.token);
        return next.handle(this.addToken(req));
      }),
      catchError(err => {
        this.isRefreshing = false;
        return from(this.handleAuthError(originalErr));
      })
    );
  }

  private logout(): void {
    const refreshToken = sessionStorage.getItem('_rt');
    sessionStorage.clear();
    this.router.navigate(['/login']);
    // Fire-and-forget: invalidate server-side session so the token can't be reused
    this.authService.logout(refreshToken).catch(() => { });
  }

}
